import React, { memo, useState, useEffect } from 'react'
import { AutoTagging } from 'components/forms/autocomplete/AutoTagging'
import { FeedbackMessage } from 'components/marketplace/FeedbackMessage'
import moment from 'moment-timezone'
import PropTypes from 'prop-types'
import { Calendar, Time } from 'components/forms'
import { formatDateForApi } from 'utils/formatDatetime'
import { FORMATS } from 'constants/datetime'

const isCallbackValid = ({ callbackDate: date, callbackTime: time }) =>
  date &&
  time &&
  moment.tz(`${date} ${time}`, FORMATS.datetimeInputFormat, FORMATS.defaultTimeZone).isAfter()

const changeCallback = (current, change) => {
  const nextState = { ...current, ...change }
  nextState.callbackValid = !nextState.showCallback || isCallbackValid(nextState)
  return nextState
}

const useNote = (defaultState = { body: '', showCallback: false, callbackValid: true }) => {
  const [state, setState] = useState({ ...defaultState })

  const modifiers = {
    changeBody: body => setState(current => ({ ...current, body })),
    changeDate: ({ date: callbackDate = '' }) =>
      setState(current => changeCallback(current, { callbackDate })),
    changeTime: callbackTime => setState(current => changeCallback(current, { callbackTime })),
    toggleCallback: () =>
      setState(current =>
        changeCallback(current, {
          showCallback: !current.showCallback,
          callbackDate: moment().format(FORMATS.dateFormat),
          callbackTime: moment().add(1, 'm').format(FORMATS.militaryTimeFormat),
        })
      ),
    reset: () => setState({ ...defaultState }),
  }

  return [state, modifiers]
}

export const NoteInput = memo(({ error, onSubmitNote, sending }) => {
  const [state, modifiers] = useNote()
  const { body, callbackDate, callbackTime, callbackValid, showCallback } = state
  const { changeBody, changeDate, changeTime, reset, toggleCallback } = modifiers

  useEffect(() => {
    if (!sending && !error) reset()
  }, [sending])

  const submit = () => {
    onSubmitNote(state)
    changeBody('')
  }

  return (
    <div className='notes-input'>
      <div className='mb4'>
        <AutoTagging
          placeholder='Add note...'
          name='note'
          data-test='note-body'
          value={body}
          onChange={changeBody}
        />
      </div>
      <div className='row no-gutter-edges'>
        <div className='col-xs-6 col-md-6 col-lg-3 col-xl-3 notes-submit'>
          <button
            className='btn-primary btn-full-width'
            disabled={!body || !callbackValid || sending}
            data-test='note-submit'
            onClick={submit}>
            {sending ? 'Adding note…' : 'Add Note'}
          </button>
        </div>
        <div className='col-xs-6 col-md-6 col-lg-3 col-xl-3 notes-callback'>
          <button
            className='btn-tertiary btn-full-width'
            onClick={toggleCallback}
            data-test='note-callback-toggle'>
            {showCallback ? 'Cancel' : 'Add callback'}
          </button>
        </div>
        {showCallback && (
          <>
            <div className='col-xs-7 col-md-7 col-lg-4 col-xl-4 notes-calendar'>
              <Calendar
                name='callbackDate'
                value={callbackDate}
                size='small'
                placeholder='Date'
                date={callbackDate}
                dateTimeChangeToState={changeDate}
                options={{ enableTime: false, formatDateForApi }}
                dataTest='callback-date'
              />
            </div>
            <div className='col-xs-5 col-md-5 col-lg-2 col-xl-2 notes-time'>
              <Time
                id='callbackTime'
                name='callbackTime'
                value={callbackTime}
                onChange={changeTime}
                dataTest='callback-time'
              />
            </div>
            {!callbackValid && (
              <div className='col-xs-12'>
                <FeedbackMessage type='warning' message='Call back time must be in the future.' />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
})

NoteInput.propTypes = {
  onSubmitNote: PropTypes.func.isRequired,
  sending: PropTypes.bool,
  error: PropTypes.string,
}

NoteInput.defaultProps = {
  sending: false,
  error: null,
}
